import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, X } from 'lucide-react';
import { menuLabel } from '@/lib/impersonationMenus';

/**
 * Shown at the top of every page while an admin/TRFer is impersonating another user.
 * The banner is rendered inside MainLayout so it appears on all protected pages.
 *
 * #2937: it is the FIRST ROW of the content column, in the layout flow, not a
 * fixed overlay. Fixed at `top-16` it sat over the family header's pill row,
 * the consent strip and the page eyebrow, and the pinned rail clipped its
 * start, hiding the name. In the flow it pushes the language row, the header
 * and <main> down by its own height, whatever that height is, and the rail sits
 * beside it, so the rail needs no offset for it (ui-ux guideline §2.10).
 *
 * The name must always show. The sentence wraps (an email breaks anywhere) and
 * clamps at two lines. The name comes before the email in every catalog (zh
 * puts the operator first), so what a clamp cuts is the email's tail. The whole
 * sentence is the row's `title`. On a narrow screen the Stop button wraps under
 * the sentence instead of squeezing it.
 */
export function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useAuth();
  const { t } = useTranslation('shell');

  if (!impersonation) return null;

  const { targetUser, allowedMenus, originalUser } = impersonation;
  const name = targetUser.profile?.first_name
    ? `${targetUser.profile.first_name} ${targetUser.profile.last_name ?? ''}`.trim()
    : targetUser.email;
  const values = { name, email: targetUser.email, actor: originalUser.profile?.first_name ?? originalUser.email };

  return (
    <div
      data-testid="impersonation-banner"
      className="flex-shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2 bg-status-warn-solid text-status-warn-solid-fg px-4 py-2 md:px-6 lg:px-8 shadow-md"
    >
      <div className="flex min-w-0 flex-1 basis-56 items-start gap-3">
        <Eye className="mt-1 h-4 w-4 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p
            className="m-0 line-clamp-2 text-body font-medium [overflow-wrap:anywhere]"
            title={t('impersonation.banner', values).replace(/<\/?strong>/g, '')}
          >
            <Trans t={t} i18nKey="impersonation.banner" values={values} components={{ strong: <strong /> }} />
          </p>
          {/* Named exemption (#745, DECISIONS 2026-08-13): these badges sit on
              the solid-warn ground, where no tint tone exists — the alpha wash
              stays, at text-label (11px), never text-meta (10px-on-tint). */}
          {allowedMenus.length > 0 && (
            <div className="mt-1 hidden flex-wrap gap-1 sm:flex">
              {allowedMenus.map((m) => (
                <Badge key={m} className="bg-status-warn-solid-fg/15 text-status-warn-solid-fg text-label">{menuLabel(m)}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="ms-auto border-status-warn-solid-fg/40 bg-status-warn-solid-fg/10 text-status-warn-solid-fg hover:bg-status-warn-solid-fg/20 flex-shrink-0"
        onClick={stopImpersonation}
      >
        <X className="h-3.5 w-3.5 me-1" />
        {t('impersonation.stop')}
      </Button>
    </div>
  );
}
